import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Alert
} from '@mui/material';
import { logger } from "../../../utils/logger";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { postFormData, getWithAuth, deleteWithAuth, buildApiUrl } from '@/utils/apiClient';
import { useIsMobile } from '../../../hooks/useMediaQuery';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EncounterPhotoUpload = ({ noteId, patientId, onClose, open = true, onPhotoUploaded = (_id?: string) => {} }: { noteId: any; patientId: any; onClose: any; open?: boolean; onPhotoUploaded?: (documentId?: string) => void }) => {
  const isMobile = useIsMobile();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  const fetchExistingPhotos = useCallback(async () => {
    if (!noteId) return;
    setLoadingPhotos(true);
    try {
      const resp = await getWithAuth(`/notes/${noteId}/documents?document_type=encounter_photo`);
      if (resp.ok) {
        const data = await resp.json();
        setExistingPhotos(data.documents || []);
      }
    } catch (err) {
      logger.error('Failed to fetch existing photos:', err);
    } finally {
      setLoadingPhotos(false);
    }
  }, [noteId]);

  useEffect(() => {
    if (noteId && open) {
      fetchExistingPhotos();
    }
  }, [noteId, open, fetchExistingPhotos]);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPEG, PNG)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setError(null);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', 'encounter_photo');
      if (noteId) {
        formData.append('note_id', noteId);
      }
      if (patientId) {
        formData.append('patient_id', patientId);
      }

      const resp = await postFormData('/documents/upload', formData);

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await resp.json();
      toast.success('Photo uploaded successfully!');
      setSelectedFile(null);
      
      if (onPhotoUploaded && result.document_id) {
        onPhotoUploaded(result.document_id);
      }
      
      fetchExistingPhotos();
    } catch (err) {
      logger.error('Upload failed:', err);
      setError(err.message || 'Failed to upload photo');
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (documentId) => {
    try {
      const resp = await deleteWithAuth(`/documents/${documentId}`);
      if (resp.ok) {
        toast.success('Photo deleted');
        setExistingPhotos(prev => prev.filter(p => p.id !== documentId));
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (err) {
      logger.error('Delete failed:', err);
      toast.error('Failed to delete photo');
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    onClose?.();
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth sx={{ zIndex: 1500 }} fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Encounter Photos</Typography>
          <IconButton onClick={handleCancel} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Take a photo or upload an image to attach to this encounter note.
        </Typography>

        {!selectedFile ? (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Upload Photo</Typography>
                <Typography variant="body2" color="text.secondary">
                  JPEG or PNG, max 10MB
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() => cameraInputRef.current?.click()}
              >
                <CameraIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Take Photo</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use device camera
                </Typography>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Preview: {selectedFile.name}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                }}
              />
              <IconButton
                onClick={handleClearSelection}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
                size="small"
               aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {(selectedFile.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
        )}

        {existingPhotos.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Attached Photos ({existingPhotos.length})
            </Typography>
            <Grid container spacing={2}>
              {existingPhotos.map((photo) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={photo.id}>
                  <Card sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={buildApiUrl(`/documents/${photo.id}`)}
                      alt={photo.filename}
                      sx={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                      }}
                    />
                    <IconButton
                      onClick={() => handleDeletePhoto(photo.id)}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                      size="small"
                     aria-label="Delete">
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        p: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {photo.filename}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {loadingPhotos && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {uploading && (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Uploading photo...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={uploading}>
          {selectedFile ? 'Cancel' : 'Close'}
        </Button>
        {selectedFile && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading}
            startIcon={<CloudUploadIcon />}
          >
            Upload Photo
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EncounterPhotoUpload;
